import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const fallbackIntervalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const listenersRef = useRef(new Set());

  useEffect(() => {
    let isMounted = true;

    const connectWebSocket = () => {
      // Don't reconnect if component is unmounted or already connecting
      if (!isMounted || (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING)) {
        return;
      }

      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
      }

      // Use relative WebSocket URL in production (through Nginx), or explicit URL in development
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = process.env.REACT_APP_WS_URL || 
        (process.env.NODE_ENV === 'production' 
          ? `${wsProtocol}//${window.location.host}/ws`
          : `${wsProtocol}//${window.location.hostname}:5000`);
      wsRef.current = new WebSocket(wsHost);

      wsRef.current.onopen = () => {
        if (!isMounted) {
          wsRef.current.close();
          return;
        }
        console.log('WebSocket connected for real-time updates');
        setIsConnected(true);
        // Clear fallback polling if WebSocket connects
        if (fallbackIntervalRef.current) {
          clearInterval(fallbackIntervalRef.current);
          fallbackIntervalRef.current = null;
        }
      };

      wsRef.current.onmessage = (event) => {
        if (!isMounted) return;
        try {
          const data = JSON.parse(event.data);
          // Notify all listeners
          listenersRef.current.forEach(listener => {
            if (listener && typeof listener === 'function') {
              listener(data);
            }
          });
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onerror = (error) => {
        if (!isMounted) return;
        console.error('WebSocket error:', error);
        setIsConnected(false);
        // Fallback to polling if WebSocket fails
        if (!fallbackIntervalRef.current) {
          fallbackIntervalRef.current = setInterval(() => {
            if (isMounted) {
              // Trigger a refresh event for listeners
              listenersRef.current.forEach(listener => {
                if (listener && typeof listener === 'function') {
                  listener({ type: 'polling_refresh' });
                }
              });
            }
          }, 10000);
        }
      };

      wsRef.current.onclose = () => {
        if (!isMounted) return;
        console.log('WebSocket disconnected, falling back to polling');
        setIsConnected(false);
        // Fallback to polling if WebSocket disconnects
        if (!fallbackIntervalRef.current) {
          fallbackIntervalRef.current = setInterval(() => {
            if (isMounted) {
              // Trigger a refresh event for listeners
              listenersRef.current.forEach(listener => {
                if (listener && typeof listener === 'function') {
                  listener({ type: 'polling_refresh' });
                }
              });
            }
          }, 10000);
        }
        // Try to reconnect after 5 seconds (only if still mounted)
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMounted) {
            connectWebSocket();
          }
        }, 5000);
      };
    };

    connectWebSocket();

    return () => {
      isMounted = false;
      // Copy ref values to local variables for cleanup
      const currentListeners = listenersRef.current;
      const currentReconnectTimeout = reconnectTimeoutRef.current;
      const currentWs = wsRef.current;
      const currentFallbackInterval = fallbackIntervalRef.current;
      
      if (currentReconnectTimeout) {
        clearTimeout(currentReconnectTimeout);
        reconnectTimeoutRef.current = null;
      }
      if (currentWs) {
        currentWs.close();
        wsRef.current = null;
      }
      if (currentFallbackInterval) {
        clearInterval(currentFallbackInterval);
        fallbackIntervalRef.current = null;
      }
      if (currentListeners) {
        currentListeners.clear();
      }
    };
  }, []);

  const addListener = (listener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  };

  return (
    <WebSocketContext.Provider value={{ isConnected, addListener }}>
      {children}
    </WebSocketContext.Provider>
  );
};

